import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import { CheckCheck, BellOff } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { notifTimeAgo, notifRecordPath, notifIconMeta } from "@/lib/notif";

const TABS = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
  { key: "leads", label: "Leads" },
  { key: "assets", label: "Assets" },
  { key: "brokers", label: "Brokers" },
  { key: "tasks", label: "Tasks" },
  { key: "system", label: "System" },
];
const PAGE = 20;

export default function NotificationsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState("all");

  const { data, fetchNextPage, hasNextPage, isFetching, refetch } = useInfiniteQuery({
    queryKey: ["notifications-page", tab],
    queryFn: async ({ pageParam = 0 }) =>
      (await api.get("/notifications", {
        params: { category: tab, include_acknowledged: true, limit: PAGE, skip: pageParam },
      })).data,
    getNextPageParam: (lastPage, pages) =>
      lastPage.length === PAGE ? pages.length * PAGE : undefined,
  });
  const items = (data?.pages || []).flat();

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["notif-unread-count"] });
    qc.invalidateQueries({ queryKey: ["notif-list"] });
    refetch();
  };
  const ackOne = useMutation({
    mutationFn: async (id) => api.post(`/notifications/${id}/acknowledge`),
    onSuccess: refresh,
  });
  const readAll = useMutation({
    mutationFn: async () => api.post("/notifications/read-all"),
    onSuccess: refresh,
  });

  const onOpen = (n) => {
    if (!n.acknowledged) ackOne.mutate(n.id);
    const path = notifRecordPath(n);
    if (path) navigate(path);
  };

  return (
    <div className="p-6 sm:p-8 max-w-3xl" data-testid="notifications-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="label-eyebrow">Activity</div>
          <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">Notifications</h1>
        </div>
        <Button variant="outline" size="sm" onClick={() => readAll.mutate()} data-testid="notifications-mark-all-read">
          <CheckCheck className="h-4 w-4 mr-1.5" /> Mark all as read
        </Button>
      </div>

      <div className="flex gap-1.5 mt-5 overflow-x-auto scrollbar-thin pb-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              tab === t.key ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
            data-testid={`notif-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-4 bg-white border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="notifications-list">
        {items.length === 0 && !isFetching ? (
          <div className="py-16 text-center text-slate-400" data-testid="notifications-empty">
            <BellOff className="h-8 w-8 mx-auto mb-2 opacity-60" />
            <div className="text-sm">No notifications</div>
          </div>
        ) : (
          items.map((n) => {
            const { Icon, tint } = notifIconMeta(n.record_type);
            return (
              <button
                key={n.id}
                onClick={() => onOpen(n)}
                className={`w-full text-left flex items-start gap-3 px-4 py-3 hover:bg-slate-50 ${n.acknowledged ? "" : "bg-amber-50/40"}`}
                data-testid={`notif-row-${n.id}`}
              >
                <span className={`shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${tint}`}>
                  <Icon className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center justify-between gap-2">
                    <span className={`text-sm truncate ${n.acknowledged ? "text-slate-700" : "font-semibold text-slate-900"}`}>
                      {!n.acknowledged && <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-500 mr-1.5 align-middle" />}
                      {n.title}
                    </span>
                    <span className="text-[11px] text-slate-400 shrink-0 font-mono-num">{notifTimeAgo(n.created_at)}</span>
                  </span>
                  <span className="block text-xs text-slate-600 mt-0.5 line-clamp-1">{n.message}</span>
                  {n.record_name && <span className="block text-[11px] text-slate-500 mt-0.5">{n.record_type}: {n.record_name}</span>}
                </span>
              </button>
            );
          })
        )}
      </div>

      {hasNextPage && (
        <div className="mt-4 text-center">
          <Button variant="outline" onClick={() => fetchNextPage()} disabled={isFetching} data-testid="notifications-load-more">
            {isFetching ? "Loading…" : "Load more"}
          </Button>
        </div>
      )}
    </div>
  );
}
